import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { projectMfaSettingsApi } from '../services/projectMfaSettingsApi';
import type { ProjectMfaSettings, ProjectMfaSettingsUpdate } from '../types/projectMfaSettings';

export const projectMfaSettingsKeys = {
  all: ['project-mfa-settings'] as const,
  detail: (projectId: string) => [...projectMfaSettingsKeys.all, projectId] as const,
};

export function useProjectMfaSettings(projectId: string | undefined) {
  return useQuery({
    queryKey: projectMfaSettingsKeys.detail(projectId!),
    queryFn: async (): Promise<ProjectMfaSettings> => {
      const response = await projectMfaSettingsApi.get(projectId!);
      if (!response.success) {
        throw new Error(response.error || 'Failed to load MFA settings');
      }
      return response.data;
    },
    enabled: !!projectId,
  });
}

export function useUpdateProjectMfaSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      data,
    }: {
      projectId: string;
      data: ProjectMfaSettingsUpdate;
    }) => {
      const response = await projectMfaSettingsApi.update(projectId, data);
      if (!response.success) {
        throw new Error(response.error || 'Failed to update MFA settings');
      }
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: projectMfaSettingsKeys.detail(variables.projectId),
      });
    },
  });
}
